import { type AnalyseResult, SCREEN_LABELS } from '../lib/types'
import AlternativesPanel from './AlternativesPanel'
import Disclaimer from './Disclaimer'
import EmailCapture from './EmailCapture'

interface Props {
  result: AnalyseResult
}

export default function ExposureResults({ result }: Props) {
  const { exposures, topOffenders, alternatives, unmatchedTickers, allocationSum } = result
  const maxExposure = Math.max(...exposures.map((e) => e.exposure_pct), 0.0001)
  const allocationOff = Math.abs(allocationSum - 100) > 0.01

  return (
    <section className="results" aria-live="polite">
      <h2>Portfolio exposure</h2>

      {unmatchedTickers.length > 0 && (
        <div className="banner banner-warn">
          We don't have holdings data for: <strong>{unmatchedTickers.join(', ')}</strong>. These were
          excluded from the analysis.
        </div>
      )}
      {allocationOff && (
        <div className="banner banner-info">
          Your allocations add up to {allocationSum.toFixed(1)}%, not 100%. Exposure figures are shown
          as a share of your <em>whole</em> portfolio, so the remaining{' '}
          {(100 - allocationSum).toFixed(1)}% is treated as unscreened.
        </div>
      )}

      <div className="exposure-list">
        {exposures.map((e) => (
          <div className="exposure-item" key={e.screen}>
            <div className="exposure-label">
              <span>{SCREEN_LABELS[e.screen]}</span>
              <strong>{e.exposure_pct.toFixed(2)}%</strong>
            </div>
            <div className="bar-track">
              <div
                className="bar-fill"
                style={{ width: `${(e.exposure_pct / maxExposure) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <h3>Top contributing holdings</h3>
      {topOffenders.length === 0 ? (
        <p className="muted">
          No holdings in your portfolio match the selected screens.
        </p>
      ) : (
        <table className="offenders">
          <thead>
            <tr>
              <th>Company</th>
              <th>Sector</th>
              <th>Screens</th>
              <th className="num">% of portfolio</th>
            </tr>
          </thead>
          <tbody>
            {topOffenders.slice(0, 15).map((o) => (
              <tr key={o.company_isin}>
                <td>
                  {o.name}
                  {o.ticker ? <span className="muted"> ({o.ticker})</span> : null}
                </td>
                <td>{o.sector ?? '—'}</td>
                <td>{o.screens.map((s) => SCREEN_LABELS[s]).join(', ')}</td>
                <td className="num">{o.contribution_pct.toFixed(3)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AlternativesPanel alternatives={alternatives} />

      <EmailCapture />

      <Disclaimer />
    </section>
  )
}
